import { produce } from 'immer';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  setValidationErrors,
  showErrorToast,
} from '../../../../../../common/global/helpers';
import Button from '../../../../../../common/widgets/Button';
import Icons from '../../../../../../common/widgets/Icons';
import Input from '../../../../../../common/widgets/Input';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { ErrorState, ErrorType } from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { searchProductService } from '../../../../services/products/search';
import SuggestionSavedDialog from './components/SuggestionSavedDialog';

const NoSearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);
  const [suggestion, setSuggestion] = useState('');
  const [showSaveSuccessDialog, setShowSaveSuccessDialog] = useState(false);
  const [errors, setErrors] = useState<ErrorState>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const helpPhoneLink = customerInfo.isSavingsUser
    ? resources.company.savingsPhoneLink
    : resources.company.phoneLink;
  const helpPhone = customerInfo.isSavingsUser
    ? resources.company.savingsPhone
    : resources.company.phone;
  const helpMailLink = customerInfo.isSavingsUser
    ? resources.company.savingsMailLink
    : resources.company.mailLink;
  const helpMail = customerInfo.isSavingsUser
    ? resources.company.savingsMail
    : resources.company.mail;

  const handleGoToHomePageClick = () => {
    navigate('/');
  };

  const handleSendSuggestion = async () => {
    setIsSubmitting(true);
    const response = await searchProductService.addRecommendation(suggestion);
    if (!response.hasErrors) {
      setSuggestion('');
      setShowSaveSuccessDialog(true);
    } else if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, setErrors);
    } else {
      showErrorToast(
        translate(resources.admin.employees.details.general.errorAddEmployee),
      );
    }
    setIsSubmitting(false);
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  return (
    <section className="section-main section-full search-no-results-page">
      <div></div>
      <div className="container">
        <div className="no-results-content">
          <div className="no-results-header">
            <h1 className="searches-no-results-title">
              {translate(resources.searchPage.noResults.title, {
                '0': `"${searchParams.get('searchTerm')}"`,
              })}
            </h1>
            <span className="no-results-message">
              {translate(resources.searchPage.noResults.message)}
            </span>
          </div>

          <div className="no-results-body">
            <div className="suggest-box">
              <div className="input-wrapper">
                <Input
                  name="suggestion"
                  label={translate(resources.searchPage.noResults.suggestLabel)}
                  value={suggestion}
                  onChange={e => setSuggestion(e.target.value as string)}
                  errors={errors?.suggestion}
                  clearErrors={() => clearErrors('suggestion')}
                />
              </div>
              <div className="disclaimer">
                {translate(resources.searchPage.noResults.disclaimer)}
              </div>
            </div>

            <Button
              className="btn btn-solid btn-lg btn-full"
              onClick={handleSendSuggestion}
              loading={isSubmitting}
            >
              {translate(resources.searchPage.noResults.button)}
            </Button>
          </div>

          <div className="no-results-footer">
            <Button className="btn btn-link" onClick={handleGoToHomePageClick}>
              <span className="btn-icon">
                <Icons.ArrowButton />
              </span>
              <span className="btn-text">
                {translate(resources.searchPage.noResults.backLink)}
              </span>
            </Button>
          </div>
        </div>
      </div>
      <div className="section-help">
        {translate(resources.cart.help.message1)}{' '}
        <Link to="/help" target="_blank" className="section-help-strong">
          {' '}
          {translate(resources.cart.help.linkLabel)}
        </Link>{' '}
        {translate(resources.cart.help.message2)}
        <br />
        <Link to={translate(helpPhoneLink)}>{translate(helpPhone)}</Link>{' '}
        {translate(resources.cart.help.message3)}{' '}
        <Link to={translate(helpMailLink)}>{translate(helpMail)}</Link>{' '}
      </div>
      {showSaveSuccessDialog && (
        <SuggestionSavedDialog close={() => setShowSaveSuccessDialog(false)} />
      )}
    </section>
  );
};
export default NoSearchResults;
